import { ChangeEvent, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { ConfirmDeleteModal } from "@components/CatalogIndex";
import EditableDataTable from "@components/EditableDataTable";
import type { Column } from "@components/EditableDataTable/types/column";
import { useV2ListPhasesForAccountQuery } from "@generated";
import { TermTypeToggle } from "./TermTypeToggle";
import { emptyTermRow } from "../types";
import type { TermFormRow, TermType } from "../types";

export interface TermsEditorProps {
  termType: TermType;
  terms: TermFormRow[];
  onChangeTermType: (next: TermType) => void;
  onChangeTerms: (terms: TermFormRow[]) => void;
}

// What EditableDataTable receives per row: the term plus the `id`/`position` it
// needs for drag-reorder. `id` is the row's stable client key (string), so it
// replaces TermFormRow's numeric server `id` (unused here).
type TermRecord = Omit<TermFormRow, "id"> & { id: string; position: number };

export const TermsEditor = ({
  termType,
  terms,
  onChangeTermType,
  onChangeTerms,
}: TermsEditorProps): JSX.Element => {
  const [pendingType, setPendingType] = useState<TermType | null>(null);

  // Active phases for milestone-term selection. (Limitation: a milestone term
  // referencing an archived phase will show no label until reassigned.)
  const { data: phasesData } = useV2ListPhasesForAccountQuery({
    filter: { active: true },
  });
  const phaseOptions = (phasesData?.data ?? []).map((p) => ({
    value: Number(p.id),
    label: p.attributes?.name ?? "",
  }));

  const handleTypeChange = (next: TermType) => {
    if (next === termType) return;
    if (terms.length > 0) {
      setPendingType(next);
    } else {
      onChangeTermType(next);
    }
  };

  const confirmTypeChange = () => {
    if (pendingType) {
      onChangeTerms([]);
      onChangeTermType(pendingType);
    }
    setPendingType(null);
  };

  const updateRow = (key: string, patch: Partial<TermFormRow>) =>
    onChangeTerms(terms.map((t) => (t.key === key ? { ...t, ...patch } : t)));

  const removeRow = (key: string) =>
    onChangeTerms(terms.filter((t) => t.key !== key));

  const addRow = () => onChangeTerms([...terms, emptyTermRow()]);

  const total = terms.reduce((sum, t) => sum + (t.percentageDue ?? 0), 0);

  // EditableDataTable hands back the reordered records (sorted by their new
  // position). Map them back to our term objects by key; position is derived
  // from array order at save time, so no position field is stored on a term.
  const handleReorder = (updated: TermRecord[]) =>
    onChangeTerms(
      updated
        .map((r) => terms.find((t) => t.key === r.id))
        .filter((t): t is TermFormRow => Boolean(t))
    );

  const records: TermRecord[] = terms.map((t, i) => ({
    ...t,
    id: t.key,
    position: i + 1,
  }));

  const columns: Column[] = [
    {
      id: "description",
      header: () => "Description",
      cell: (record: TermRecord) => (
        <TextField
          label={null}
          id={`term-description-${record.key}`}
          value={record.description}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateRow(record.key, { description: e.target.value })
          }
        />
      ),
    },
    {
      id: "amount",
      header: () => (termType === "percent" ? "Percentage" : "Phase"),
      cell: (record: TermRecord) =>
        termType === "percent" ? (
          <FormNumberFieldV2
            label={null}
            id={`term-percentage-${record.key}`}
            value={record.percentageDue}
            min={0}
            max={100}
            step={1}
            placeholder="%"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateRow(record.key, {
                percentageDue:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
        ) : (
          <RequiredReactSelect
            label={null}
            value={phaseOptions.find((o) => o.value === record.phaseId)}
            onChange={(opt: any) =>
              updateRow(record.key, { phaseId: opt?.value ?? null })
            }
            options={phaseOptions}
            placeholder="Select a phase…"
          />
        ),
    },
    {
      id: "remove",
      header: () => "",
      cell: (record: TermRecord) => (
        <button
          type="button"
          className="btn btn-link text-danger"
          aria-label="Remove term"
          onClick={() => removeRow(record.key)}
        >
          <FontAwesomeIcon icon={faTrashCan} />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="adminFormSectionTitle">Schedule</div>
      <TermTypeToggle value={termType} onChange={handleTypeChange} />

      {terms.length === 0 ? (
        <p className="text-muted">No terms yet.</p>
      ) : (
        <EditableDataTable
          records={records}
          columns={columns}
          config={{
            classForTable: "termsTable",
            // Only enable reordering when there's more than one row to move.
            dragAndDrop: terms.length > 1,
          }}
          setData={handleReorder}
        />
      )}

      {termType === "percent" && terms.length > 0 && (
        <div className="termsTotalRow">
          <span className={`termsTotal ${total === 100 ? "" : "invalid"}`}>
            Total: {total}% / 100%
          </span>
        </div>
      )}

      <button
        type="button"
        className="btn btn-outline-primary"
        onClick={addRow}
      >
        + Add Term
      </button>

      <ConfirmDeleteModal
        open={pendingType !== null}
        title="Change term type?"
        message="Changing the term type will clear the current terms. Continue?"
        confirmLabel="Clear & switch"
        onConfirm={confirmTypeChange}
        onCancel={() => setPendingType(null)}
      />
    </>
  );
};

export default TermsEditor;
